import { create } from "zustand"

import { SurveyGroupVariant } from "../components/onboarding/customOnboardSurveyData"
import { AnswerInterface, ParentDataInterface } from "../types"
import { cohorts } from "../util/experiments"
import { history } from "../util/router"
import { localStorageItems, sessionManager } from "../util/storage"

export enum OnboardingStep {
  ParentingQuestionnaire,
  AddChildOrJoinFamily,
  CoparentCode,
  AddChildren,
  CustomPlan,
  ParentName,
  ParentNicknameAvatar,
  EmailPassword,
  CreateAccountWithCredentials,
}

export const ChildStep = Object.freeze({
  ChildInfo: 0,
  QuestionnaireDisclaimer: 1,
  Questionnaire: 2,
})

interface OnboardContextStore {
  isCoparent: boolean
  setIsCoparent: (value: boolean) => void
  coparentCode: string
  setCoparentCode: (value: string) => void

  parentData: ParentDataInterface
  creatingPlan: boolean
  setCreatingPlan: (value: boolean) => void
  isCreatingUser: boolean
  setIsCreatingUser: (value: boolean) => void
  currentStep: OnboardingStep
  childStep: number
  parentQuestionnaireStep: number
  setParentQuestionnaireStep: (value: number) => void
  setChildStep: (value: number) => void
  setParentData: (
    fieldName:
      | "name"
      | "nickname"
      | "lastName"
      | "avatar"
      | "parentQuestionnaireMapping"
      | "childrenData",
    value: any
  ) => void
  setStep: (value: number) => void
  nextStep: () => void
  previousStep: () => void
  isSubmittingData: boolean
  setIsSubmittingData: (value: boolean) => void
  postAnswerScreen: AnswerInterface | null
  setPostAnswerScreen: (value: AnswerInterface | null) => void

  parentSurveyVariant: SurveyGroupVariant
  setParentSurveyVariant: (value: SurveyGroupVariant) => void
  childSurveyVariant: SurveyGroupVariant
  setChildSurveyVariant: (value: SurveyGroupVariant) => void

  isCustomOnboarding: boolean
  setIsCustomOnboarding: (value: boolean) => void
}

const useOnboardContext = create<OnboardContextStore>((set) => ({
  isCoparent: false,
  setIsCoparent: (value: boolean) => set({ isCoparent: value }),
  coparentCode: "",
  setCoparentCode: (value: string) =>
    set({ coparentCode: value.toUpperCase() }),

  parentData: {
    name: "",
    lastName: "",
    nickname: "",
    avatar: null,
    parentQuestionnaireMapping: {},
    childrenData: [],
  },
  setParentData: (fieldName, value) =>
    set((state) => ({
      parentData: { ...state.parentData, [fieldName]: value },
    })),

  creatingPlan: false,
  setCreatingPlan: (value: boolean) => set({ creatingPlan: value }),
  isCreatingUser: false,
  setIsCreatingUser: (value: boolean) => set({ isCreatingUser: value }),
  // STEPS
  currentStep: OnboardingStep.ParentingQuestionnaire,
  childStep: 0,
  parentQuestionnaireStep: 0,
  setParentQuestionnaireStep: (value: number) =>
    set({ parentQuestionnaireStep: value }),

  setChildStep: (value: number) => set({ childStep: value }),
  setStep: (value: number) => set({ currentStep: value }),
  nextStep: () => {
    set((state: OnboardContextStore) => {
      if (state.currentStep === OnboardingStep.CoparentCode) {
        return { currentStep: OnboardingStep.ParentName }
      } else if (
        state.currentStep === OnboardingStep.ParentNicknameAvatar &&
        sessionManager.hasRefreshToken()
      ) {
        return { currentStep: OnboardingStep.CreateAccountWithCredentials }
      } else if (
        state.currentStep === OnboardingStep.AddChildren &&
        localStorage.getItem(
          localStorageItems.onboardingCustomPlanExperiment
        ) === cohorts.control
      ) {
        return { currentStep: OnboardingStep.ParentName }
      } else return { currentStep: state.currentStep + 1 }
    })
  },
  previousStep: () => {
    set((state: OnboardContextStore) => {
      if (state.currentStep === 0) {
        sessionManager.clearAuthTokens()
        history.goBack()
        return {}
      } else if (
        state.currentStep === OnboardingStep.ParentName &&
        state.isCoparent
      ) {
        return { currentStep: OnboardingStep.CoparentCode }
      } else if (
        state.currentStep === OnboardingStep.ParentName &&
        localStorage.getItem(
          localStorageItems.onboardingCustomPlanExperiment
        ) === cohorts.control
      ) {
        return { currentStep: OnboardingStep.AddChildren }
      } else if (state.currentStep === OnboardingStep.AddChildren) {
        return { currentStep: OnboardingStep.AddChildOrJoinFamily }
      } else return { currentStep: state.currentStep - 1 }
    })
  },

  // Data submit
  isSubmittingData: false,
  setIsSubmittingData: (value: boolean) => set({ isSubmittingData: value }),
  postAnswerScreen: null,
  setPostAnswerScreen: (value: AnswerInterface | null) =>
    set({ postAnswerScreen: value }),

  parentSurveyVariant: SurveyGroupVariant.PARENT_CHORES,
  setParentSurveyVariant: (value: SurveyGroupVariant) =>
    set({ parentSurveyVariant: value }),
  childSurveyVariant: SurveyGroupVariant.CHILD_CHORES,
  setChildSurveyVariant: (value: SurveyGroupVariant) =>
    set({ childSurveyVariant: value }),

  isCustomOnboarding: false,
  setIsCustomOnboarding: (value: boolean) => set({ isCustomOnboarding: value }),
}))

export default useOnboardContext
